/**
 * media-upload — put a picked image through the real upload op and hand back
 * the URL to store on the record (2026-08-07, Pat).
 *
 * `<ImageDropField>` gives us a data URL straight off the file picker. Two
 * things have to happen before that can be a record field:
 *
 *  1. DOWNSCALE. A phone photo is 3–6 MB; nothing in a roster row needs more
 *     than an avatar. We cap the long edge at 512px and re-encode, which lands
 *     typical avatars at 30–80 KB — small enough that the upload is instant and
 *     the stored asset is cheap wherever it ends up.
 *  2. UPLOAD. The bytes go to `uploadMediaAsset` (POST /v1/admin/media/uploads)
 *     and the returned `url` is what the record carries. The image is never
 *     smuggled into the record payload itself.
 *
 * Anything already stored (an http(s) URL, or a URL we got back from a previous
 * upload) passes straight through — re-saving a record must not re-upload the
 * same photo.
 */
import { uploadMediaAsset } from "@v0y4ge/api-client";

const MAX_EDGE = 512;

export type MediaPurpose = "staff-photo" | "resource-photo" | "brand-asset" | "other";

function isStoredUrl(value: string): boolean {
  return /^(https?:)?\/\//i.test(value) || value.startsWith("/");
}

/** Draw into a canvas at <= MAX_EDGE and re-encode. Falls back to the original. */
/** The upload contract's three accepted types, and all `downscale` may return. */
type UploadMime = "image/png" | "image/jpeg" | "image/webp";

async function downscale(dataUrl: string): Promise<{ data: string; contentType: UploadMime }> {
  const wantsAlpha = dataUrl.startsWith("data:image/png") || dataUrl.startsWith("data:image/webp");
  const type = wantsAlpha ? "image/png" : "image/jpeg";
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("image decode failed"));
      el.src = dataUrl;
    });
    const edge = Math.max(img.naturalWidth, img.naturalHeight) || MAX_EDGE;
    const scale = edge > MAX_EDGE ? MAX_EDGE / edge : 1;
    const w = Math.max(1, Math.round((img.naturalWidth || MAX_EDGE) * scale));
    const h = Math.max(1, Math.round((img.naturalHeight || MAX_EDGE) * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { data: dataUrl, contentType: type };
    ctx.drawImage(img, 0, 0, w, h);
    return { data: canvas.toDataURL(type, 0.85), contentType: type };
  } catch (e) {
    console.warn("media downscale failed, uploading original:", e);
    /* Was `m[1]` — the source mime, passed through verbatim. On the happy path a GIF
     * is re-encoded to JPEG and never reaches the server as GIF, so the only way
     * `image/gif` got out was here, when decode failed. The server's mime normalizer
     * has no GIF entry, so that upload came back 415. Only report a type the contract
     * accepts; anything else is the original bytes, which we describe as the closest
     * accepted type rather than one the server will reject. */
    const m = /^data:([^;,]+)/.exec(dataUrl);
    const source = m ? m[1] : type;
    const accepted: UploadMime[] = ["image/png", "image/jpeg", "image/webp"];
    return { data: dataUrl, contentType: accepted.includes(source as UploadMime) ? (source as UploadMime) : type };
  }
}

/**
 * Returns the URL to store, or "" when the field was cleared.
 * Throws when the upload fails — the caller must NOT pretend it saved.
 */
export async function uploadImageField(value: string | null | undefined, opts: {
  purpose: MediaPurpose;
  ownerId?: string | null;
  filename?: string;
}): Promise<string> {
  const raw = (value ?? "").trim();
  if (!raw) return "";
  if (isStoredUrl(raw)) return raw;
  if (!raw.startsWith("data:image/")) return raw; // not something we can upload; leave it alone
  const { data, contentType } = await downscale(raw);
  const sizeBytes = Math.round((data.length - (data.indexOf(",") + 1)) * 3 / 4);
  const asset = await uploadMediaAsset({
    purpose: opts.purpose,
    ownerId: opts.ownerId ?? null,
    filename: opts.filename || `${opts.purpose}.${contentType === "image/png" ? "png" : "jpg"}`,
    /* No cast: `downscale` now returns `UploadMime`, which is the contract's union.
     * `image/gif` used to be asserted here — type-valid by construction, so nothing
     * failed until the server answered 415. */
    contentType,
    data,
    sizeBytes,
  });
  return asset.url;
}
